import { take } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Store, createSelector } from '@ngrx/store';
import { IProduct } from '@cemex-core/cmx-api/models/product/models/product.interface';
import { IDocumentItem } from '@cemex-core/cmx-api/models/sales-document-management';
import { Unit } from '@cemex-core/cmx-api/models/common/unit';

import { OnFieldChanged } from '@ordersnproducts/modules/shared/modules/configurable-form/models/on-field-changed';
import { OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers/index';
import { ConfigurableFormComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/containers/configurable-form/configurable-form.component';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { getProductId } from '@ordersnproducts/modules/order-builder/modules/specification/helpers/product.helper';
import { unitConversion } from '@ordersnproducts/modules/order-builder/modules/specification/helpers/unit-conversion.helper';
import { getAvailableContractsBalance } from '@ordersnproducts/modules/order-builder/modules/specification/selectors/contract.selector';
import { getProductUnitsWithDefaults } from '@ordersnproducts/modules/order-builder/modules/specification/selectors/units.selector';
import { ContractBalance } from '@ordersnproducts/modules/order-builder/modules/specification/models/contract-balance';
import { RemainingBalanceValidator } from '@ordersnproducts/modules/order-builder/modules/specification/services/validators/remaining-balance.validator';

const getContractsBalanceAndProductUnits = createSelector(
  getAvailableContractsBalance,
  getProductUnitsWithDefaults,
  (contractsBalance, productUnitsWithDefaults) => {
    return { contractsBalance, productUnitsWithDefaults };
  },
);

@Injectable()
export class HandleRemainingBalanceOpenPlatformCommand implements OnFieldChanged {
  constructor(private store: Store<OrderBuilderState>) {}
  onFieldChanged(value, formGroup: UntypedFormGroup, form: ConfigurableFormComponent) {
    const quantityCtrl = formGroup.get(SpecFieldNames.QUANTITY);
    const contractCtrl = formGroup.get(SpecFieldNames.CONTRACT_SELECT);
    const invoiceCtrl = formGroup.get(SpecFieldNames.CONTRACT_ANTICIPATED_INVOICE);

    // Validate contract balance only if theres a contract selected
    if ((contractCtrl && contractCtrl.value) || (invoiceCtrl && invoiceCtrl.value)) {
      this.store
        .select(getContractsBalanceAndProductUnits)
        .pipe(take(1))
        .subscribe(({ contractsBalance, productUnitsWithDefaults }) => {
          // Retrieve remaining balance from contract or anticipated invoice
          const balance = contractCtrl.value
            ? contractsBalance[contractCtrl.value]
            : this.invoiceContractBalance(invoiceCtrl.value);
          const selectedProductId = getProductId(formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value as IProduct);
          const units = productUnitsWithDefaults.units.alternativeUnits[selectedProductId];
          const selectedUnit =
            units && units.find((unit) => formGroup.get(SpecFieldNames.UNIT).value === unit.alternativeUnit.unitId);
          const validators = [...form.config.find((config) => config.name === SpecFieldNames.QUANTITY).validation];
          quantityCtrl.clearValidators();
          if (balance && selectedUnit && balance.unitOfMeasure.unitId === selectedUnit.baseUnit.unitId) {
            const conversion = unitConversion(selectedUnit, balance.amount);
            validators.push(RemainingBalanceValidator.validate(conversion));
          }
          quantityCtrl.setValidators(validators);
        });
    } else {
      quantityCtrl.clearValidators();
      quantityCtrl.setValidators(form.config.find((config) => config.name === SpecFieldNames.QUANTITY).validation);
    }

    // Re-calculate control validity
    quantityCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  /**
   * Returns ContractBalance item from anticipated invoice
   * @param invoice
   */
  private invoiceContractBalance(invoice: IDocumentItem): ContractBalance {
    return {
      amount: Number(invoice.openedQuantity),
      unitOfMeasure: {
        unitDesc: invoice.unitOfMeasure.unitDesc,
        unitId: Number(invoice.unitOfMeasure.unitId),
        unitCode: invoice.unitOfMeasure.unitCode,
      } as Unit,
    };
  }
}
